import React, {useEffect, useMemo, useState} from 'react'
import {useQueryClient} from '@tanstack/react-query';
import {useNavigate, useSearchParams} from 'react-router-dom';
import LoadingBanner from '../../components/LoadingBanner';
import EmptyBanner from '../../components/EmptyBanner';
import { Switch, Dropdown } from "antd";

import SupplierJobBoardCard from './components/SupplierJobBoardCard';
import TablePagination from '../../components/TablePagination';
import JobBoardFilter from '../talent-job-board/components/JobBoardFilter';
import {useCreateAgencyOpportunityMutation, useTalentResourceFilters} from "../../models/AgencyOpportunity";
import ErrorBanner from "../../components/ErrorBanner";
import {useAPI} from "../../config/API";
import {useMount} from "react-use";
import {useAuth} from "../../providers/AuthProvider";

const DisciplineCategories = TB.map([...laravel.discipline_categories], cat => {
    const option = TB.pick(cat, ['id', 'name', 'disciplines', 'display_order']);
    // TODO Change with updated discipline filters for client/talent
    const disciplines = cat.disciplines;//TB.filter(cat.disciplines, ['is_applicable', true]);
    option.disciplines = TB.map(disciplines, discipline => TB.pick(discipline, ['id', 'name', 'display_order']))
    option.value = option.id;

    return option
});

const Disciplines = TB.map(DisciplineCategories, category => {
    return {
        ...category,
        options: category.disciplines.map(discipline => {
            return {
                ...discipline,
                value: discipline.id
            }
        })
    }
})

const SORT_TYPES = {
    posted_at_desc: 'Date Posted (new to old)',
    posted_at_asc: 'Date Posted (old to new)',
    payout_amount_desc: 'Est Payout (high to low)',
    payout_amount_asc: 'Est Payout (low to high)',
    client_name_asc: 'Client (A to Z)',
    client_name_desc: 'Client (Z to A)',
    proximity_asc: 'Location (near to far)',
};

const TimeZones = TB.TimeZonesSplit()
const PAGE_COUNT = 15;

const SupplierJobBoard = props => {
    const storageKey = `jobBoardParams`


    const filtersFromStorage = (storageFilters, talentResources) => {
        if (!storageFilters) {
            let storage = JSON.parse(
                localStorage.getItem(storageKey)
                || '{}'
            );

            storageFilters = storage.storageFilters || {};
        }

        let disciplineOptions = TB.flatten(TB.map(Disciplines, 'options'));
        if (storageFilters.disciplines && storageFilters.disciplines?.length !== disciplineOptions?.length) {
            storageFilters.disciplines = TB.map(
                storageFilters.disciplines,
                id => TB.find(disciplineOptions, ['id', parseInt(id)])
            )
        } else {
            storageFilters.disciplines = [];
        }

        if (!storageFilters.time_zones) {
            storageFilters.time_zones = [...TimeZones[0].options]
        }
        // console.log('storageFilters.matching_talent_users', storageFilters.matching_talent_users)
        if (talentResources && storageFilters.matching_talent_users && storageFilters.matching_talent_users?.length !== talentResources?.length) {
            // console.log('restttingggggggggggg')
            storageFilters.matching_talent_users = TB.filter(TB.map(
                storageFilters.matching_talent_users,
                id => TB.find(talentResources, ['id', parseInt(id)])
            ), ['is_disabled', false])
        }else {
            storageFilters.matching_talent_users = storageFilters.matching_talent_users || [];
        }

        if (!storageFilters.sort_by) {
            storageFilters.sort_by = TB.keys(SORT_TYPES)[0];
        }
        if (!storageFilters.per_page) {
            storageFilters.per_page = PAGE_COUNT;
        }

        return storageFilters
    }

    const {auth} = useAuth();
    const [filters, setFilters] = useState(null);
    const [appliedFilters, setAppliedFilters] = useState(null);

    const navigate = useNavigate();
    const [_editingFilter, setEditingFilter] = useState('');
    const [formErrors, setFormErrors] = useState(null);
    const [isDisabled, setIsDisabled] = useState(false);
    const persistStorage = () => {
        let storageFilters = {...appliedFilters};
        if (storageFilters.discipline_categories) {
            storageFilters.discipline_categories = TB.map(storageFilters.discipline_categories, 'id')
        }
        if (storageFilters.disciplines) {
            storageFilters.disciplines = TB.map(storageFilters.disciplines, 'id')
        }
        if (storageFilters.matching_talent_users) {
            storageFilters.matching_talent_users = TB.map(storageFilters.matching_talent_users, 'id')
        }
        localStorage.setItem(storageKey, JSON.stringify(
            {storageFilters}
        ));
    }

    const queryClient = useQueryClient();
    const [searchParams, setSearchParams] = useSearchParams();
    const currentPage = useMemo(() => parseInt(searchParams.get('page')) || 1, [searchParams?.toString()]);

    const setCurrentPage = (pageNumber) => {
        const params = new URLSearchParams(searchParams);

        if (TB.isNaN(parseInt(pageNumber))) {
            return
        }
        // console.log('setcurrentpage', { pageNumber, current: params.get('page') }, parseInt(params.get('page')) !== parseInt(pageNumber))
        if (TB.isNaN(parseInt(params.get('page'))) || parseInt(params.get('page')) !== parseInt(pageNumber)) {
            params.set('page', pageNumber);

            setSearchParams(params, { preventScrollReset: true });
        }
    }

    const getParams = () => {
        if (!appliedFilters) return ;
        let {
            disciplines,
            time_zones,
            sort_by,
            sort_dir,
            matching_talent_users,
            ...mapFilters
        } = { ...appliedFilters }

        if (sort_by && TB.endsWith(sort_by, '_asc')) {
            sort_by = TB.replace(sort_by, '_asc', '');
            sort_dir = 'asc'
        }
        if (sort_by && TB.endsWith(sort_by, '_desc')) {
            sort_by = TB.replace(sort_by, '_desc', '');
            sort_dir = 'desc'
        }

        mapFilters.disciplines = TB.map(disciplines, 'value').join(',');
        mapFilters.time_zones = TB.map(time_zones, 'value').join(',');
        mapFilters.matching_talent_users = TB.map(matching_talent_users, 'value').join(',')
        // console.log('getParams', mapFilters)
        return {
            ...mapFilters,
            page: currentPage,
            page_count: PAGE_COUNT,
            sort_dir,
            sort_by
        }
    }

    const talentResourceFilters = useTalentResourceFilters();

    useEffect(() => {
        if (!talentResourceFilters?.isLoading && talentResourceFilters?.data?.length) {
            let updatedFilters = filtersFromStorage(null,
                TB.map(talentResourceFilters?.data, user => {
                return {
                    ...user,
                    name: user.fullName(),
                    value: user.id,
                    disabled: !user.is_active || !user.registered_at || !user.offers_byo_work || !user.talent_discipline_ids?.length
                }
            }));
            setFilters({...updatedFilters})
            setAppliedFilters({...updatedFilters})
        }
    }, [talentResourceFilters?.isLoading, talentResourceFilters?.data]);

    useMount(() => {
        if (!auth?.clientOrg?.()?.total_supplied_matching_talent_users) {
            TB.alert("warning", "No matching talent resources available",
                "Note: You won't be able to pursue any jobs until you have talent resources who are available for hire by role.", [{title: 'Ok'}]
            )
        }
    })

    const {
        data,
        isFetching,
        isLoading,
        isRefetching,
        error,
        refetch,
        ...result
    } = useAPI(['fetchJobBoardForTalent', getParams()],
         {
            enabled: !!filters && !!appliedFilters && !talentResourceFilters?.isLoading && !!talentResourceFilters?.data,
            onSuccess: async (data) => {
                console.log('onSuccess', data);

                if (!TB.isNaN(parseInt(currentPage)) && data.last_page < parseInt(currentPage)) {

                    console.log('onSuccessreset')
                    await setCurrentPage(1);
                }

                persistStorage();
                // setAppliedParams(getParams())
            }
        })

    const availableDisciplines = useMemo(() => {
        if (!filters?.only_matching) {
            return Disciplines
        }
        let matchedTalent = filters?.matching_talent_users?.length ?
            filters?.matching_talent_users : talentResourceFilters?.data;


        if (!matchedTalent?.length) {
            return Disciplines;
        }

        let matchedTalentDisciplines = TB.uniq(
            TB.flatMap(matchedTalent || [], 'talent_discipline_ids')
        )

        let available = TB.filter(
            TB.map(Disciplines,
                category => {
                    return {
                        ...category,
                        options: TB.map(category.options, option => {
                            return {
                                ...option,
                                disabled: !matchedTalentDisciplines.includes(parseInt(option.value))
                            }
                        })
                    }
                }
            ),
            category => category.options?.length
        )
        return available
    }, [filters?.only_matching, filters?.matching_talent_users, talentResourceFilters?.data]);

    const talentResourceOptions = useMemo(() => {
        return TB.map(talentResourceFilters?.data || [], user => {
            return {
                ...user,
                name: (
                    <span className="flex flex-row justify-between gap-4">
                        <span>
                            {user.fullName()}
                        </span>
                        {!user.registered_at ? (
                            <i className="text-xxs text-danger">Not Registered</i>
                        ) : (
                            !user.talent_discipline_ids?.length ? (
                                <i className="text-xxs text-danger">Missing Roles</i>
                            ) : ((!user.offers_byo_work || !user.is_active) && (
                                <i className="text-xxs text-danger">Unavailable</i>
                            ))
                        )}
                    </span>
                ),
                value: user.id,
                disabled: !user.is_active || !user.registered_at || !user.offers_byo_work || !user.talent_discipline_ids?.length
            }
        })
    }, [talentResourceFilters?.data, talentResourceFilters?.isLoading]);
    const defaultFilters = (type) => {
        switch (type) {
            case 'disciplines':
                return TB.filter(
                    TB.flatMap(availableDisciplines, 'options'), option => !option.disabled
                );

            case 'matching_talent_users':
                if (!talentResourceFilters?.data?.length) {
                    return []
                }
                return TB.filter(talentResourceOptions, option => !option.disabled);

        }
    }
    const canClearFilters = () => {
        const currentFilters = TB.filter({ ...filters }, filter => TB.isArray(filter) && filter.length > 0);

        return Object.keys(currentFilters).length > 0
    }

    const clearFilters = () => {
        setFilters({ sort_by: filters.sort_by || TB.keys(SORT_TYPES)[0] });
        setAppliedFilters({ sort_by: filters.sort_by || TB.keys(SORT_TYPES)[0] });
    }

    const canApplyFilters = () => {
        const currentFilters = TB.pickBy({ ...filters }, filter =>
            TB.isArray(filter) ? filter : filter
        );
        const currentAppliedFilters = TB.pickBy({ ...appliedFilters }, filter =>
            TB.isArray(filter) ? filter : filter
        );

        return !TB.isEqual(currentAppliedFilters, currentFilters)
    }

    const applyFilters = async (resetPage = true) => {

        setEditingFilter(null);
        setFilters({ ...filters, page: resetPage ? 1 : undefined });

        setAppliedFilters({ ...filters, page: resetPage ? 1 : undefined });
    }

    const createFilter = (label, subLabel, editingFilter, options, editingDisabled) => {
        const value = filters[editingFilter]?.length ? [...filters[editingFilter]] : defaultFilters(editingFilter);
        return (
            <JobBoardFilter
                key={label}
                value={value}
                options={TB.isArray(options) ? options : undefined}
                label={label}
                editingFilter={editingFilter}
                subLabel={subLabel}
                right={false}
                onToggleEditing={() => setEditingFilter(editingFilter === _editingFilter ? null : editingFilter)}
                isEditing={_editingFilter === editingFilter}
                formErrors={!!formErrors && formErrors[editingFilter]}
                isDisabled={isLoading || isDisabled || editingDisabled}
                onChange={async (items) => {
                    if (TB.keys(formErrors).length) {
                        await setFormErrors(null);
                    }

                    setFilters({ ...filters, [editingFilter]: items })
                }}
            />
        )
    }

    const createDefaultFilter = (editingFilter, options, selected) => {
        if (!TB.isNaN(parseInt(selected))) {
            selected = parseInt(selected)
        }
        let selectedOption = TB.find(options, option => TB.isEqual(option.value, selected))
        if (!selectedOption) {
            selectedOption = TB.first(options);
        }

        return (
            <Dropdown
                menu={{
                    items: options,
                    selectedKeys: [selected],
                    onClick: e => setFilters({ ...filters, [editingFilter]: e.key })
                    // disabled: props.disabled,
                }} 
                trigger={['click']}
                autoFocus
                disabled={isLoading}
            >
                <button
                    type="button"
                    onClick={e => e.preventDefault()}
                    className={TB.mapClass("dropdown-toggle filter-button filter-sm", {
                        'disabled': isLoading
                    })}
                >
                    <span>{selectedOption.display || selectedOption.name}</span>
                </button>
            </Dropdown>
        )
    }

    const createAgencyOpportunity = useCreateAgencyOpportunityMutation();

    const { last_page, total, jobs } = data || {};

    const renderPagination = () => {
        return last_page > 1 && !isFetching && (
            <div className="mb-3">
                <TablePagination
                    isDisabled={props.disabled || isFetching || isRefetching || isLoading}
                    handlePageClick={setCurrentPage}
                    currentPage={currentPage}
                    pagesCount={last_page}
                    total={total}
                    perPage={PAGE_COUNT}
                />
            </div>
        )
    }

    return (
        <div className="supplier-job-board pb-12">

            {filters && (
                <div className="job-board-filters-container">
                    <div className="type-filters">

                        <div className="row-content">


                        </div>
                    </div>


                    <div className="criteria-filters">
                        <div className="row-content">
                            {!talentResourceFilters.isLoading && talentResourceFilters.data?.length > 0 &&
                                <JobBoardFilter
                                    value={filters.matching_talent_users?.length ? [...filters.matching_talent_users] : defaultFilters('matching_talent_users')}
                                    options={talentResourceOptions}
                                    label={'Matched Resources'}
                                    right={false}
                                    onToggleEditing={() => setEditingFilter(_editingFilter === 'matching_talent_users' ? null : 'matching_talent_users')}
                                    isEditing={_editingFilter === 'matching_talent_users'}
                                    formErrors={!!formErrors && formErrors['matching_talent_users']}
                                    isDisabled={isLoading || isDisabled}
                                    onChange={async (items) => {
                                        if (TB.keys(formErrors).length) {
                                            await setFormErrors(null);
                                        }

                                        let updatedFilters = { ...filters, matching_talent_users: items };

                                        if (!items.length) {
                                            updatedFilters.only_matching = false;
                                            updatedFilters.disciplines = [];
                                        }else if (filters.disciplines?.length) {
                                            let availableTalentDisciplines = TB.uniq(TB.flatMap(items, 'talent_discipline_ids'));
                                            updatedFilters.disciplines = TB.filter(updatedFilters.disciplines, discipline => availableTalentDisciplines.includes(discipline.value))
                                        }

                                        await setFilters(updatedFilters);
                                        if (!updatedFilters.only_matching) {
                                            setEditingFilter(null)
                                        }
                                    }}
                                    className={!filters?.only_matching ? "disabled" : ""}
                                    renderLabel={(
                                        <div className={TB.mapClass("flex flex-row items-center gap-1.5 flex-nowrap clickable z-10", {
                                            "pointer-events-none": isLoading,
                                            'pointer-events-auto': !isLoading
                                        })} onClick={e => {
                                            if (filters.only_matching) {
                                                setEditingFilter(_editingFilter === 'matching_talent_users' ? null : 'matching_talent_users')
                                            }
                                        }}>

                                            <Switch
                                                onChange={async (checked) => {
                                                    console.log('swith', checked)
                                                    let updatedFilters = {...filters, only_matching: checked}
                                                    if (!checked) {
                                                        updatedFilters.matching_talent_users = defaultFilters('matching_talent_users');
                                                        let availableTalentDisciplines = TB.uniq(TB.flatMap(updatedFilters.matching_talent_users, 'talent_discipline_ids'));
                                                        updatedFilters.disciplines = TB.filter(updatedFilters.disciplines, discipline => availableTalentDisciplines.includes(discipline.value))
                                                    }else {
                                                        if (updatedFilters.disciplines?.length) {
                                                            updatedFilters.disciplines = TB.flatMap(availableDisciplines, 'options')
                                                        }
                                                    }

                                                    await setFilters(updatedFilters);

                                                    if (checked) {
                                                        setEditingFilter('matching_talent_users')
                                                    }else if(_editingFilter === 'matching_talent_users') {
                                                        setEditingFilter(null)
                                                    }
                                                }}
                                                checked={!!filters.only_matching}
                                                size="small"/>
                                            <span>
                                                Matched Resources <span>({filters.matching_talent_users?.length || defaultFilters('matching_talent_users')?.length})</span>
                                            </span>
                                        </div>
                                    )}
                                />
                            }
                            {createFilter(
                                filters.only_matching ? 'Matched Roles' : 'Wripple Roles',
                                null,
                                'disciplines',
                                availableDisciplines
                            )}
                            {createFilter('Location', 'Time Zones', 'time_zones', TimeZones)}
                            {createDefaultFilter(
                                'sort_by',
                                TB.map(SORT_TYPES, (name, value) => {
                                    return {
                                        label: name,
                                        key: value,
                                        name, value, display: (
                                            <span>
                                                Sort By: {name}
                                            </span>
                                        )
                                    }
                                }),
                                filters.sort_by || TB.keys(SORT_TYPES)[0]
                            )}
                        </div>
                    </div>

                    <div className="filter-actions">
                        <div className="row-content">
                            <button
                                onClick={applyFilters}
                                className="mx-2 btn btn-light btn-primary apply-filters-btn"
                                disabled={isLoading || !canApplyFilters() || isDisabled}
                            >
                                Search
                            </button>

                            <button
                                onClick={clearFilters}
                                className="mx-2 btn btn-dark apply-filters-btn"
                                disabled={isLoading || !canClearFilters() || isDisabled}
                            >
                                Clear
                            </button>
                        </div>
                    </div>
                </div>

            )}
            <div className="px-4">
                {renderPagination()}

                {!!isFetching && (<LoadingBanner message="Loading" />)}

                {!isFetching && !error && (
                    <div className="p-4 divide-y divide-wrippleLightGrayBg">

                        {!jobs?.length ? (
                            <EmptyBanner message="No Jobs Found" style={{ paddingTop: '200px' }} />
                        ) : (
                            jobs.map(job => (
                                <SupplierJobBoardCard
                                    key={job.id} job={job}
                                    onStore={() => createAgencyOpportunity.mutate(job)}
                                    isDisabled={createAgencyOpportunity.isLoading}/>
                            ))
                        )}
                    </div>
                )}
                {!isFetching && error && (
                    <ErrorBanner onRetry={refetch}/>
                )}

                {renderPagination()}

            </div>
        </div>
    )
}

export default SupplierJobBoard;